// Dynamic scrollbar color based on scroll position
export const initScrollbarColorChanger = () => {
  // Define the four colors from navbar hover states
  const colors = [
    { color: "#9b6bff", name: "Personal Coaching" }, // Purple - 0-25%
    { color: "#ff69b4", name: "Relationships" }, // Pink - 25-50%
    { color: "#4caf50", name: "Groups" }, // Green - 50-75%
    { color: "#2196f3", name: "Businesses" }, // Blue - 75-100%
  ];

  // Create style element for dynamic scrollbar styling
  const styleEl = document.createElement("style");
  styleEl.id = "dynamic-scrollbar-style";
  document.head.appendChild(styleEl);

  const updateScrollbarColor = () => {
    // Calculate scroll percentage
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;
    const scrollPercent = (scrollTop / scrollHeight) * 100;

    // Determine which color to use with smooth interpolation
    let currentColor;
    let fromColor, toColor, ratio;

    if (scrollPercent < 25) {
      // Interpolate between purple and pink (0-25%)
      fromColor = colors[0].color;
      toColor = colors[1].color;
      ratio = scrollPercent / 25;
      currentColor = interpolateColor(fromColor, toColor, ratio);
    } else if (scrollPercent < 50) {
      // Interpolate between pink and green (25-50%)
      fromColor = colors[1].color;
      toColor = colors[2].color;
      ratio = (scrollPercent - 25) / 25;
      currentColor = interpolateColor(fromColor, toColor, ratio);
    } else if (scrollPercent < 75) {
      // Interpolate between green and blue (50-75%)
      fromColor = colors[2].color;
      toColor = colors[3].color;
      ratio = (scrollPercent - 50) / 25;
      currentColor = interpolateColor(fromColor, toColor, ratio);
    } else {
      // Stay at blue (75-100%)
      currentColor = colors[3].color;
    }

    // Create darker shade for hover
    const darkerColor = adjustBrightness(currentColor, -20);
    const darkestColor = adjustBrightness(currentColor, -40);

    // Update CSS custom properties and scrollbar styles
    styleEl.textContent = `
      :root {
        --scrollbar-color: ${currentColor};
        --scrollbar-color-hover: ${darkerColor};
        --scrollbar-color-active: ${darkestColor};
      }

      /* Webkit browsers */
      ::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, ${currentColor} 0%, ${adjustBrightness(currentColor, 10)} 100%) !important;
        border-radius: 10px;
        border: 2px solid #f1f3f5;
        transition: all 0.3s ease;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, ${darkerColor} 0%, ${currentColor} 100%) !important;
        border: 2px solid #e9ecef;
      }

      ::-webkit-scrollbar-thumb:active {
        background: linear-gradient(135deg, ${darkestColor} 0%, ${darkerColor} 100%) !important;
      }

      /* Firefox */
      * {
        scrollbar-color: ${currentColor} #f1f3f5 !important;
      }
    `;
  };

  // Helper function to interpolate between two colors
  function interpolateColor(color1, color2, ratio) {
    // Clamp ratio between 0 and 1
    ratio = Math.max(0, Math.min(1, ratio));

    // Parse color1
    const c1 = color1[0] === "#" ? color1.slice(1) : color1;
    const r1 = parseInt(c1.substring(0, 2), 16);
    const g1 = parseInt(c1.substring(2, 4), 16);
    const b1 = parseInt(c1.substring(4, 6), 16);

    // Parse color2
    const c2 = color2[0] === "#" ? color2.slice(1) : color2;
    const r2 = parseInt(c2.substring(0, 2), 16);
    const g2 = parseInt(c2.substring(2, 4), 16);
    const b2 = parseInt(c2.substring(4, 6), 16);

    // Interpolate each channel
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);

    // Return hex color
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  // Helper function to adjust color brightness
  function adjustBrightness(color, amount) {
    const usePound = color[0] === "#";
    const col = usePound ? color.slice(1) : color;
    const num = parseInt(col, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00ff) + amount;
    let b = (num & 0x0000ff) + amount;

    r = Math.max(Math.min(255, r), 0);
    g = Math.max(Math.min(255, g), 0);
    b = Math.max(Math.min(255, b), 0);

    return (
      "#" + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)
    );
  }

  // Initial update
  updateScrollbarColor();

  // Update on scroll with throttling for performance
  let ticking = false;
  window.addEventListener("scroll", () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateScrollbarColor();
        ticking = false;
      });
      ticking = true;
    }
  });

  // Update on resize
  window.addEventListener("resize", updateScrollbarColor);
};

// Cleanup function
export const cleanupScrollbarColorChanger = () => {
  const styleEl = document.getElementById("dynamic-scrollbar-style");
  if (styleEl) {
    styleEl.remove();
  }
};
